import React, { MutableRefObject, useRef, useState } from 'react';
// @ts-ignore
import { useJsonToCsv } from 'react-json-csv';
import { saveAs } from 'file-saver';
import { Button } from 'antd';
import { QueryResponse, store, LinkWithNodes } from '../../utils/types';
import { connect } from 'react-redux';
import { cropNodeForExport, getHexColorFromHsl } from '../../utils/helpers';
import { FileExcelOutlined, FileImageOutlined } from '@ant-design/icons';
import { exportComponentAsPNG } from 'react-component-export-image';
import LegendBar from './LegendBar';

type Props = {
  queryResponse: QueryResponse;
  graphWrapperRef: MutableRefObject<any | undefined>;
  visualize: boolean;
  coloredField: any;
};
const Export = ({
  queryResponse,
  graphWrapperRef,
  visualize,
  coloredField,
}: Props) => {
  const [loadings, setLoadings] = useState<any>({});
  const [legendVisible, setLegendVisible] = useState(false);
  const legendWrapperRef = useRef<any>(null);
  const { saveAsCsv } = useJsonToCsv();
  const handleExportGraphPng = () => {
    setLoadings((loads: any) => ({ ...loads, graphPng: true }));
    if (graphWrapperRef.current) {
      const canvas = graphWrapperRef.current.querySelector('canvas');
      if (canvas) {
        canvas.toBlob(function (blob: any) {
          saveAs(blob, 'export-graph.png');
        });
      }
    }
    setLoadings((loads: any) => ({ ...loads, graphPng: false }));
  };

  const handleExportLegendPng = () => {
    setLoadings((loads: any) => ({ ...loads, legendPng: true }));
    if (legendWrapperRef.current) {
      exportComponentAsPNG(legendWrapperRef);
    }
    setLoadings((loads: any) => ({ ...loads, legendPng: false }));
  };

  const handleExportNodesCsv = () => {
    setLoadings((loads: any) => ({ ...loads, nodesCsv: true }));
    let data = queryResponse.nodes;

    data = data.map((node: any) => {
      const croppedNode = cropNodeForExport(node);
      croppedNode.index = node.indexClone;
      croppedNode.color = '';
      croppedNode.coloredField = '';
      if (coloredField) {
        croppedNode.coloredField = coloredField.key;

        if (node.hasOwnProperty(coloredField.key)) {
          croppedNode.color = getHexColorFromHsl(
            coloredField.colors[node[coloredField.key]][0]
          );
        }
      }

      return croppedNode;
    });

    const fields = Object.keys(queryResponse.nodesInfo);

    fields.push(
      'symbol',
      'index',
      'location',
      'Degree',
      'coloredField',
      'color'
    );

    saveAsCsv({
      data,
      fields: fields.reduce((a, v) => ({ ...a, [v]: v }), {}),
      fileformat: 'txt',
      filename: 'nodes',
      separator: '\t',
    });
    setLoadings((loads: any) => ({ ...loads, nodesCsv: false }));
  };

  const handleExportEdgesCsv = () => {
    setLoadings((loads: any) => ({ ...loads, edgesCsv: true }));
    setTimeout(() => {
      const data = (queryResponse.links as LinkWithNodes[]).map(
        (link: LinkWithNodes) => ({
          source: link.source.symbol,
          target: link.target.symbol,
          score: link.correlation,
          pvalue: link.pvalue,
          padj: link.padj,
        })
      );
      saveAsCsv({
        data,
        fields: {
          source: 'Source',
          target: 'Target',
          score: 'Score',
          pvalue: 'PVal',
          padj: 'Padj',
        },
        fileformat: 'txt',
        filename: 'edges',
        separator: '\t',
      });
      setLoadings((loads: any) => ({ ...loads, edgesCsv: false }));
    }, 10);
  };

  return (
    <>
      <div className="flex flex-col space-y-4">
        {visualize && (
          <>
            <Button
              loading={loadings.graphPng}
              icon={<FileImageOutlined />}
              onClick={handleExportGraphPng}
            >
              Graph as PNG
            </Button>
          </>
        )}
        <Button
          loading={loadings.legendPng}
          icon={<FileImageOutlined />}
          onClick={() => {
            setLegendVisible(true);
            setTimeout(() => {
              handleExportLegendPng();
              setLegendVisible(false);
            }, 0);
          }}
        >
          Legend as PNG
        </Button>
        <Button
          icon={<FileExcelOutlined />}
          loading={loadings.nodesCsv}
          onClick={handleExportNodesCsv}
        >
          Nodes
        </Button>
        <Button
          icon={<FileExcelOutlined />}
          loading={loadings.edgesCsv}
          onClick={handleExportEdgesCsv}
        >
          Edges
        </Button>
        {legendVisible && (
          <div
            ref={legendWrapperRef}
            className="absolute p-5"
            style={{ top: -5000 }}
          >
            <LegendBar />
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state: store) => ({
  queryResponse: state.query.queryResponse,
  visualize: state.query.requestedQueryDetails?.visualize,
  coloredField: state.graphModals.coloredField,
});
export default connect(mapStateToProps)(Export);
